import {useEffect, useState} from "react";
import {Placeholder} from "react-bootstrap";
import PropTypes from "prop-types";
import useDepartureTimes from "./use-departure-times.js";
import Alert from "../ui/alert/alert.jsx";
import FeedbackError from "@/components/ui/feedback-error/feedback-error.jsx";
import {DepartureTimeContext} from "./departure-time-context.jsx";
import Util from "../../lib/Util.jsx";
import {AnimatePresence} from "framer-motion";
import AnimatedComponent from "../ui/animated-component/animated-component.jsx";
import Intermediate from "./intermediate.jsx";
import AnimatedComponents from "../ui/animated-component/animated-components.jsx";
import Moment from "moment";

const ListDepartureTimes = ({line_id, departure_location, destination_location, variant, scope}) => {
  const {data, observations, error, isLoaded} = useDepartureTimes(line_id, variant);
  
  const [sortedDays, setSortedDays] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  
  useEffect(() => {
    // Só executa se os dados da API já tiverem chegado
    if (data && data.length > 0) {
      
      // Função async interna para permitir o uso de 'await'
      const processAndSortDays = async () => {
        try {
          // A lógica de preparação dos dados fica aqui dentro
          const uniqueDirections = data.map((item) => item.direction).filter((value, index, self) => self.indexOf(value) === index).sort();
          const uniqueDaysForDirection = uniqueDirections.map((direction) => data.filter((item) => item.direction === direction).map((item) => item.day).filter((value, index, self) => self.indexOf(value) === index));
          
          const result = await Promise.all(uniqueDaysForDirection.map(async directionItems => {
            const dayPairs = await Promise.all(
              directionItems.map(async i => {
                const dayName = await Util.convertNumberToDay(i);
                // Validação para evitar erro com 'null' ou 'undefined' no localeCompare
                if (!dayName) {
                  throw new Error(`Falha ao converter o dia para o número: ${i}`);
                }
                return [i, dayName];
              })
            );
            
            return dayPairs
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(pair => pair[0]);
          }));
          
          // 4. Se tudo der certo, atualiza o estado com os dias ordenados
          setSortedDays(result);
          
        } catch (err) {
          // 5. Se qualquer promise for rejeitada, captura o erro e atualiza o estado de erro
          console.error("Erro ao processar os dias de operação:", err);
          setProcessingError(err);
        }
      };
      
      processAndSortDays().then(() => {
      });
    }
  }, [data]); // A dependência [data] garante que o efeito rode quando os dados chegarem
  
  // Lógica de Renderização
  
  if (isLoaded) return (
    <AnimatedComponents>
      <Placeholder as="div" animation="glow" className="mt-3">
        <Placeholder xs={12} className="mb-2"/>
        <Placeholder xs={10} className="mb-2"/>
        <Placeholder xs={8}/>
      </Placeholder>
    </AnimatedComponents>
  );
  if (error) return <AnimatedComponents><FeedbackError code={error.response?.status || 500} text={error.message} type={'card'}/></AnimatedComponents>;
  if (processingError) return <AnimatedComponents><Alert variant={'danger'}><span>Ocorreu um erro ao organizar os horários. Tente novamente mais tarde.</span></Alert></AnimatedComponents>;
  if (data.length === 0) return <AnimatedComponents><Alert variant={'info'}><span>Não localizamos horários para esta linha.</span></Alert></AnimatedComponents>;
  
  // 6. Enquanto os dias estão sendo ordenados, mostramos uma mensagem
  if (!sortedDays) return <div>Organizando horários...</div>;
  
  return (
    <AnimatePresence mode={"wait"}>
      <AnimatedComponent>
        <DepartureTimeContext>
          <Intermediate
            data={data}
            observations={observations}
            line_id={line_id}
            departure_location={departure_location}
            destination_location={destination_location}
            sortedDays={sortedDays}
            type={variant?.type || "current"}
            scope={scope}
          />
        </DepartureTimeContext>
      </AnimatedComponent>
    </AnimatePresence>
  );
}

ListDepartureTimes.propTypes = {
  line_id: PropTypes.number.isRequired,
  departure_location: PropTypes.string.isRequired,
  destination_location: PropTypes.string.isRequired,
  variant: PropTypes.shape({
    type: PropTypes.oneOf(["history", "current"]),
    departureTimeData: PropTypes.oneOfType([PropTypes.string, Moment])
  }),
  scope: PropTypes.any
}

export {ListDepartureTimes};
